import React, { useEffect, useState, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { BrandLogo } from './components/BrandLogo';
import { emptyEcommBrand } from 'common/api/e-comm/models/ECommBrand';
import { Product } from 'common/api/e-comm/models/Product';
import { useApi } from 'common/modules/async-actions/useApi';
import { getProduct } from 'common/api/e-comm/products';
import Skeleton from 'react-loading-skeleton';
import { Routes } from 'routes';
import { ShopScreen } from 'features/shop-base/ShopScreen';
import { ProductAmountInput, ProductAmountInputFormData } from './components/ProductAmountInput';
import { Form, Formik } from 'formik';
import { Btn } from 'components/btn/Btn';
import { emptyCartItem, CartItem } from 'common/api/e-comm/models/CartItem';
import { selectedProductOptions } from 'common/utils/amountInput';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag } from '@fortawesome/pro-light-svg-icons';
import useMediaQueries from 'hooks/useMediaQueries';
import { SizeProp } from '@fortawesome/fontawesome-svg-core';
import { openLoader, LoadingOverlay, closeLoader } from 'components/loading-overlay/LoadingOverlay';
import { useAddProductToCart } from 'hooks/useAddProductToCart';
import { useRootSelector } from 'common/features/featuresReducer';

export interface LocalProductDetailsParams {
    productId: string;
}

const useProduct = (productId: string) => {
    const [loadProduct, , result] = useApi(getProduct);
    const [loading, setLoadingTo] = useState<boolean>(true);
    const [hasLoaded, setHasLoadedTo] = useState<boolean>(false);

    useEffect(() => {
        if (!hasLoaded) {
            loadProduct(parseInt(productId)).finally(() => {
                setHasLoadedTo(true);
                setLoadingTo(false);
            });
        }
    }, [productId, hasLoaded, loadProduct]);

    return { product: result?.data || undefined, loading };
};

export const LocalProductScreen = () => {
    return (
        <ShopScreen>
            <LocalProductView />
        </ShopScreen>
    );
};

const LocalProductView = () => {
    const { productId } = useParams<LocalProductDetailsParams>();
    const { product, loading } = useProduct(productId);
    const org = useRootSelector((state) => state.store.home.homeDetails?.defaultOrganization);

    const history = useHistory();

    if (loading) {
        return <Skeleton className="w-full my-10" height={300} />;
    }

    // CASE: product was not found -- redirect ot shop screen
    if (product?.organization !== org?.id || (!product?.isAvailable && !product && !loading)) {
        history.replace(Routes.Shop);
    }

    return (
        <div className="px-6 xd:px-16 xl:px-44 my-8 lg:my-24 gap-8 xl:gap-20 block text-sm lg:text-base">
            <main className="mt-4 lg:mt-8 max-w-3xl mx-auto">
                <HeaderSection product={product} />
                <Content product={product} />
            </main>
        </div>
    );
};

const LogoRenderer = () => {
    const [iconSize, setIconSize] = useState<SizeProp>('5x');

    useMediaQueries({
        '(max-width: 648px)': () => {
            setIconSize('3x');
        },
        '(min-width: 649px)': () => {
            setIconSize('5x');
        },
    });
    return <FontAwesomeIcon className="text-brand" icon={faTag} size={iconSize} />;
};

const HeaderSection = ({ product }: { product?: Product }) => {
    return (
        <div className="flex gap-4 sm:gap-8">
            <BrandLogo
                renderers={{ logo: LogoRenderer }}
                brand={{ ...emptyEcommBrand, imageUrl: 'dummy-text' }}
            />
            <Title>{product && product.name}</Title>
        </div>
    );
};

const Title = ({ children }: React.PropsWithChildren) => (
    <h1 className="break-all text-brand-dark lg:mt-2 text-3xl lg:text-5xl">{children}</h1>
);

const Content = ({ product }: { product?: Product }) => {
    const initialValues: CartItem = {
        ...emptyCartItem,
        selectedOptions: selectedProductOptions(product),
        productId: product?.id ?? 0,
        priceOverride: product?.price ?? null,
    };
    const { addProductToCart } = useAddProductToCart();
    const loader = useRef<LoadingOverlay>(null);

    const onSubmit = async (values: CartItem) => {
        openLoader(loader.current);
        await addProductToCart(
            product as Product,
            values as ProductAmountInputFormData,
            emptyEcommBrand
        );
        closeLoader(loader.current);
    };
    return product ? (
        <div
            data-cnstrc-product-detail
            data-cnstrc-item-id={product.id}
            data-cnstrc-item-name={product.name}
            data-cnstrc-item-price={product.unitPrice}
            className="mt-2 mb-10 sm:my-10 text-grey-2"
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit}>
                <Form>
                    <div className="mt-8">
                        <ProductAmountInput
                            product={product}
                            label={'Card Value'}
                            hideQuantityField={false}
                            uiSettings={{ totalMargin: 'mt-auto sm:mt-0 sm:mb-auto' }}
                        />
                    </div>
                    <div className="block text-center sm:text-right mt-14 mb-8">
                        <Btn className="w-48 sm:ml-4">Add to Cart</Btn>
                    </div>
                </Form>
            </Formik>
        </div>
    ) : null;
};
